import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { collection, doc, getDoc, getDocs, limit, orderBy, query, startAfter, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { FaCalendarAlt, FaTag } from 'react-icons/fa';
import { FiBox, FiCalendar, FiExternalLink, FiShield, FiUser, FiUsers } from 'react-icons/fi';
import CollectiblePreviewSurface from '../components/CollectiblePreviewSurface';
import PurchaseProgressOverlay from '../components/PurchaseProgressOverlay';
import TopBar from '../components/TopBar';
import ArtistHeroCard from '../components/ArtistHeroCard';
import ResolvedStorageImage from '../components/ResolvedStorageImage';
import UnavailableState from '../components/UnavailableState';
import { UserContext } from '../App';
import { db, functions } from '../firebase';
import { getStripeClient } from '../services/stripe';
import layoutStyles from '../styles/layout.module.css';
import styles from './UnitDetail.module.css';
import { getUnavailableState } from '../utils/pageAvailability';
import { canUnitBeResold, formatDate, formatPrice, getViewerTimeZone } from '../utils/marketplace';
import { deriveActiveConnectStatus } from '../utils/stripeConnect';
import { timezoneLabel } from '../utils/currency';
import { ANONYMOUS_PHOTO_URL, resolvePublicProfile } from '../utils/profileIdentity';
import {
  isVideoCollectibleMedia,
  useCollectibleMediaUrls,
} from '../utils/collectibleMedia';
import { getArtistSignaturePath, getPrimaryImagePath, getUserPhotoPath, useStorageUrl } from '../utils/storageMedia';

const stripePromise = getStripeClient();
const OWNER_PAGE_SIZE = 10;
const PURCHASE_STEPS = [
  {
    label: 'Preparing the resale order',
    summary: 'We are validating the listing and reserving this unit for your checkout.',
    detail: 'Preparing the resale order and reserving the unit for you.',
  },
  {
    label: 'Confirming payment',
    summary: 'Your saved card is being authorized for the resale purchase.',
    detail: 'Confirming your payment details with Stripe.',
  },
  {
    label: 'Transferring ownership',
    summary: 'We are updating the collectible unit, history, and owner records.',
    detail: 'Transferring ownership and updating collectible records.',
  },
  {
    label: 'Refreshing your library',
    summary: 'The unit is being added to your account and transaction history.',
    detail: 'Refreshing your library and final purchase details.',
  },
];

function AddCardForm({ clientSecret, onDone }) {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;
    setSaving(true);
    setError('');
    try {
      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card: elements.getElement(CardElement) },
      });
      if (result.error) setError(result.error.message || 'Unable to save card.');
      else onDone?.();
    } catch (err) {
      setError(err?.message || 'Unable to save card.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className={styles.cardForm} onSubmit={handleSubmit}>
      <div className={styles.cardElement}>
        <CardElement options={{ hidePostalCode: true, disableLink: true, style: { base: { fontSize: '16px', color: '#0f172a', '::placeholder': { color: '#94a3b8' } } } }} />
      </div>
      {error ? <p className={styles.error}>{error}</p> : null}
      <button type="submit" disabled={!stripe || saving}>{saving ? 'Saving…' : 'Save Card'}</button>
    </form>
  );
}

function OwnerAvatar({ owner }) {
  const photoPath = getUserPhotoPath(owner);
  return (
    <div className={styles.ownerAvatar}>
      {photoPath ? (
        <ResolvedStorageImage
          path={photoPath}
          alt={owner.name}
          className={styles.ownerAvatarImage}
          fallback={<img src={owner.photoUrl || ANONYMOUS_PHOTO_URL} alt={owner.name} className={styles.ownerAvatarImage} />}
        />
      ) : <img src={owner.photoUrl || ANONYMOUS_PHOTO_URL} alt={owner.name} className={styles.ownerAvatarImage} />}
    </div>
  );
}

function buildOwnerRows(units = [], profiles = [], viewerUid = '') {
  const profileMap = new Map(
    profiles.map((profile) => [String(profile?.uid || profile?.id || '').trim(), resolvePublicProfile(profile, viewerUid)])
  );

  return units.map((unit) => {
    const ownerUid = String(unit.ownerUid || '').trim();
    const resolved = profileMap.get(ownerUid) || resolvePublicProfile({ uid: ownerUid, profileVisibility: 'private' }, viewerUid);
    return {
      id: unit.id,
      ownerUid,
      unitNumber: unit.unitNumber || null,
      purchasedAt: unit.purchasedAt || unit.createdAt || null,
      activeListingId: unit.activeListingId || null,
      ...resolved,
    };
  });
}

export default function UnitDetail() {
  const { unitId } = useParams();
  const appUser = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [unit, setUnit] = useState(null);
  const [product, setProduct] = useState(null);
  const [artist, setArtist] = useState(null);
  const [activeListing, setActiveListing] = useState(null);
  const [cards, setCards] = useState([]);
  const [selectedCardId, setSelectedCardId] = useState('');
  const [cardsError, setCardsError] = useState('');
  const [setupClientSecret, setSetupClientSecret] = useState('');
  const [setupBusy, setSetupBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [purchaseStage, setPurchaseStage] = useState(0);
  const [priceAmount, setPriceAmount] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [owners, setOwners] = useState([]);
  const [ownersLoading, setOwnersLoading] = useState(true);
  const [ownersLoadingMore, setOwnersLoadingMore] = useState(false);
  const [ownersError, setOwnersError] = useState('');
  const [ownersCursor, setOwnersCursor] = useState(null);
  const [ownersHasMore, setOwnersHasMore] = useState(false);
  const loadCards = useCallback(async () => {
    if (!appUser?.id) return;
    try {
      const callable = httpsCallable(functions, 'listPaymentMethods');
      const response = await callable({});
      const methods = Array.isArray(response?.data?.methods) ? response.data.methods : [];
      setCards(methods);
      setSelectedCardId(methods[0]?.id || '');
      setCardsError(methods.length ? '' : 'No saved cards yet.');
    } catch (err) {
      setCards([]);
      setCardsError(err?.message || 'Unable to load cards.');
    }
  }, [appUser?.id]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const unitSnap = await getDoc(doc(db, 'units', String(unitId)));
      if (!unitSnap.exists()) {
        setUnit(null);
        setProduct(null);
        setArtist(null);
        setError('Unit not found.');
        return;
      }
      const unitData = { id: unitSnap.id, ...(unitSnap.data() || {}) };
      const [productSnap, listingSnap] = await Promise.all([
        getDoc(doc(db, 'products', String(unitData.productId))),
        unitData.activeListingId ? getDoc(doc(db, 'listings', String(unitData.activeListingId))) : Promise.resolve(null),
      ]);
      if (!productSnap.exists()) {
        setUnit(null);
        setProduct(null);
        setArtist(null);
        setError('Product not found.');
        return;
      }
      const productData = { id: productSnap.id, ...(productSnap.data() || {}) };
      const artistSnap = productData.artistId ? await getDoc(doc(db, 'artists', String(productData.artistId))) : null;
      setUnit(unitData);
      setProduct(productData);
      setArtist(artistSnap?.exists() ? { id: artistSnap.id, ...(artistSnap.data() || {}) } : null);
      setActiveListing(listingSnap && listingSnap.exists() ? { id: listingSnap.id, ...(listingSnap.data() || {}) } : null);
      setError('');
    } catch (err) {
      setArtist(null);
      setError(err?.message || 'Unable to load unit.');
    } finally {
      setLoading(false);
    }
  }, [unitId]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadCards(); }, [loadCards]);

  const loadOwners = useCallback(async ({ reset = false, afterDoc = null } = {}) => {
    if (!product?.id) return;
    const setBusy = reset ? setOwnersLoading : setOwnersLoadingMore;
    setBusy(true);
    if (reset) {
      setOwners([]);
      setOwnersCursor(null);
      setOwnersHasMore(false);
      setOwnersError('');
    }

    try {
      const ownerQuery = query(
        collection(db, 'units'),
        where('productId', '==', String(product.id || '')),
        orderBy('unitNumber', 'asc'),
        ...(afterDoc ? [startAfter(afterDoc), limit(OWNER_PAGE_SIZE)] : [limit(OWNER_PAGE_SIZE)])
      );
      const unitsSnap = await getDocs(ownerQuery);
      const unitRows = unitsSnap.docs.map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() || {}) }));
      const ownerUids = [...new Set(unitRows.map((row) => String(row.ownerUid || '').trim()).filter(Boolean))];
      let profiles = [];

      if (ownerUids.length) {
        const callable = httpsCallable(functions, 'getPublicOwnerProfiles');
        const response = await callable({ uids: ownerUids });
        profiles = Array.isArray(response?.data?.profiles) ? response.data.profiles : [];
      }

      const rows = buildOwnerRows(unitRows, profiles, appUser?.id);
      setOwners((current) => (reset ? rows : [...current, ...rows]));
      setOwnersCursor(unitsSnap.docs[unitsSnap.docs.length - 1] || null);
      setOwnersHasMore(unitsSnap.docs.length === OWNER_PAGE_SIZE);
    } catch (err) {
      setOwnersError(err?.message || 'Unable to load collectors.');
    } finally {
      setBusy(false);
    }
  }, [appUser?.id, product?.id]);

  useEffect(() => {
    if (!product?.id) {
      setOwners([]);
      setOwnersLoading(false);
      setOwnersLoadingMore(false);
      setOwnersError('');
      setOwnersCursor(null);
      setOwnersHasMore(false);
      return;
    }
    loadOwners({ reset: true });
  }, [loadOwners, product?.id]);

  const unavailableState = getUnavailableState('unit', error);
  const connectStatus = useMemo(() => deriveActiveConnectStatus(appUser), [appUser]);
  const { mediaUrl: collectibleMediaUrl, playbackUrl, posterUrl } = useCollectibleMediaUrls(product || {});
  const primaryImageUrl = useStorageUrl(getPrimaryImagePath(product || {}));
  const artistSignatureUrl = useStorageUrl(getArtistSignaturePath(artist || {}));
  const mediaUrl = collectibleMediaUrl || primaryImageUrl;
  const hasPlayableCollectibleVideo = isVideoCollectibleMedia(product || {}) && Boolean(playbackUrl);
  const metadataState = unit?.collectibleMetadata || unit?.metadata || {};
  const canManage = Boolean(appUser?.id) && unit?.ownerUid === appUser.id;
  const isForSale = Boolean(activeListing?.id && activeListing?.status === 'active');
  const listingBlockedReason = String(activeListing?.salesEligibility?.blockedReason || '').trim();
  const listingBlocked = Boolean(activeListing?.salesEligibility?.blocked);
  const canBuy = false;
  const resaleUnlocked = useMemo(() => canUnitBeResold(unit || {}, product || {}), [product, unit]);
  const canResell = canManage && connectStatus.canCharge && unit?.status === 'owned' && !activeListing && metadataState.status === 'ready' && resaleUnlocked;
  const backTo = String(location.state?.backTo || '');
  const variantLabel = String(product?.variantLabel || '').trim();
  const currentOwner = owners.find((owner) => owner.ownerUid === String(unit?.ownerUid || '').trim());
  const ownerName = currentOwner?.name || 'Unknown Collector';
  const shortCid = String(metadataState.cid || '').trim();
  const metadataLabel = shortCid ? `${shortCid.slice(0, 4)}...` : 'Unavailable';
  const viewerTimezone = getViewerTimeZone();

  const handleStartSetup = async () => {
    if (!appUser?.id || !stripePromise) return;
    try {
      setSetupBusy(true);
      setError('');
      const callable = httpsCallable(functions, 'createSetupIntent');
      const response = await callable({});
      setSetupClientSecret(response?.data?.clientSecret || '');
    } catch (err) {
      setError(err?.message || 'Unable to start card setup.');
    } finally {
      setSetupBusy(false);
    }
  };

  const waitForOrderCompletion = useCallback(async (paymentIntentId) => {
    const callable = httpsCallable(functions, 'getPaymentOrderStatus');
    for (let attempt = 0; attempt < 15; attempt += 1) {
      const response = await callable({ paymentIntentId });
      const status = String(response?.data?.status || '').trim();
      if (status === 'completed') return response;
      await new Promise((resolve) => window.setTimeout(resolve, 1200));
    }
    throw new Error('Payment succeeded, but the resale is still being finalized. Please refresh in a moment.');
  }, []);

  const handleBuy = async () => {
    if (!activeListing?.id) {
      setError('This unit is not for sale.');
      return;
    }
    if (!selectedCardId) {
      setError('Choose or add a saved card.');
      return;
    }
    try {
      setBusy(true);
      setPurchaseStage(0);
      setError('');
      setSuccess('');
      const callable = httpsCallable(functions, 'purchaseListing');
      const response = await callable({ listingId: activeListing.id, paymentMethodId: selectedCardId });
      setPurchaseStage(1);
      const stripe = await stripePromise;
      const clientSecret = response?.data?.clientSecret;
      const paymentIntentId = String(response?.data?.paymentIntentId || '').trim();
      if (!clientSecret || !paymentIntentId) throw new Error('No payment client secret returned.');
      const result = await stripe.confirmCardPayment(clientSecret, { payment_method: selectedCardId });
      if (result.error) throw new Error(result.error.message || 'Payment failed.');
      setPurchaseStage(2);
      await waitForOrderCompletion(paymentIntentId);
      setPurchaseStage(3);
      await loadData();
      setSuccess('Purchase complete. Ownership has been updated.');
      navigate(`/units/${unitId}/success`, { replace: true, state: { backTo: `/units/${unitId}`, productId: product?.id || '' } });
    } catch (err) {
      setError(err?.message || 'Unable to complete purchase.');
    } finally {
      setBusy(false);
    }
  };

  const handleCreateResale = async () => {
    const parsed = Number(priceAmount || 0);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setError('Enter a valid resale price.');
      return;
    }
    try {
      setBusy(true);
      setError('');
      setSuccess('');
      const callable = httpsCallable(functions, 'createResaleListing');
      await callable({ unitId, priceAmount: parsed });
      setPriceAmount('');
      await loadData();
      setSuccess('Unit listed for resale.');
    } catch (err) {
      setError(err?.message || 'Unable to create listing.');
    } finally {
      setBusy(false);
    }
  };

  const handleCancelListing = async () => {
    if (!activeListing?.id) return;
    try {
      setBusy(true);
      setError('');
      setSuccess('');
      const callable = httpsCallable(functions, 'cancelListing');
      await callable({ listingId: activeListing.id });
      await loadData();
      setSuccess('Listing canceled.');
    } catch (err) {
      setError(err?.message || 'Unable to cancel listing.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={layoutStyles.detailPage}>
      <PurchaseProgressOverlay
        open={busy && canBuy}
        activeStep={purchaseStage}
        steps={PURCHASE_STEPS}
        title="Buying this unit"
        caption="Please keep this page open while we finalize the resale purchase."
      />
      <TopBar
        variant="back"
        backLabel="Back"
        onBack={() => (backTo ? navigate(backTo) : navigate(-1))}
        rightActionLabel="Home"
        onRightAction={() => navigate('/home')}
      />
      <div className={`${layoutStyles.detailMain} ${styles.page}`.trim()}>
        {loading ? <p className={styles.state}>Loading unit…</p> : null}
        {!loading && error && (!unit || !product) ? (
          <UnavailableState
            eyebrow="Unit Unavailable"
            title={unavailableState.title}
            message={unavailableState.message}
            onPrimary={() => navigate(-1)}
            onSecondary={() => navigate('/home')}
            secondaryLabel="Browse Artists"
          />
        ) : null}
        {!loading && !error && unit && product ? (
          <div className={styles.shell}>
            <CollectiblePreviewSurface
              hasPlayableVideo={hasPlayableCollectibleVideo}
              mediaUrl={mediaUrl}
              playbackUrl={playbackUrl}
              posterUrl={posterUrl || mediaUrl}
              backTextureUrl={artistSignatureUrl}
              title={product.title || 'Unit'}
              placeholderLabel="Unit"
            />
            <div className={styles.infoPane}>
              <span className={styles.eyebrow}>Product Unit</span>
              <ArtistHeroCard
                artist={artist}
                eyebrow="Collected From"
                subtitle="Artist identity stays attached to every owned unit"
                onClick={artist?.id ? () => navigate(`/artists/${artist.id}`) : null}
              />
              {variantLabel ? (
                <div className={styles.tierBanner}>
                  <span>{variantLabel}</span>
                </div>
              ) : null}
              <h1>{product.title || unit.productId}</h1>
              {isForSale ? <p className={styles.price}>{formatPrice(activeListing || product || {})}</p> : null}
              <p className={styles.summary}>This is the numbered collectible unit tied to the original release, ownership history, and resale rules.</p>
              {success ? <div className={styles.successBanner}>{success}</div> : null}
              {error ? <p className={styles.error}>{error}</p> : null}

              <div className={styles.metaGrid}>
                <div className={styles.metaCard}><span><FiBox /> Unit</span><strong>#{unit.unitNumber || '—'}</strong></div>
                <div className={styles.metaCard}><span><FiUser /> Status</span><strong>{isForSale ? `Owned by ${ownerName} · Listed` : `Owned by ${ownerName}`}</strong></div>
                <div className={styles.metaCard}><span><FiShield /> Variant</span><strong>{variantLabel || 'Standard'}</strong></div>
                <div className={styles.metaCard}><span><FaCalendarAlt /> Purchased</span><strong>{formatDate(unit.purchasedAt)}</strong></div>
                <div className={styles.metaCard}><span><FiCalendar /> Your Timezone</span><strong>{timezoneLabel(viewerTimezone)}</strong></div>
                <div className={styles.metaCard}><span><FaTag /> Resale Hold</span><strong>{Number(product.resaleHoldDays || 90)} days</strong></div>
                <a
                  className={`${styles.metaCard} ${styles.metaLink}`.trim()}
                  href={metadataState.url || '#'}
                  target="_blank"
                  rel="noreferrer"
                  aria-disabled={!metadataState.url}
                  onClick={(event) => {
                    if (!metadataState.url) event.preventDefault();
                  }}
                >
                  <span><FiExternalLink /> Metadata</span>
                  <strong>{metadataLabel}</strong>
                </a>
              </div>

              {canResell ? (
                <section className={styles.block}>
                  <h2>List for Resale</h2>
                  <input value={priceAmount} onChange={(event) => setPriceAmount(event.target.value)} type="number" min="1" step="0.01" placeholder={`Resale price ${product.currency || 'USD'}`} />
                  <button type="button" className={styles.buyButton} onClick={handleCreateResale} disabled={busy}>{busy ? 'Listing…' : 'Create Listing'}</button>
                </section>
              ) : null}

              {canManage && !activeListing && unit?.status === 'owned' && metadataState.status === 'ready' && resaleUnlocked && !connectStatus.canCharge ? (
                <section className={styles.block}>
                  <h2>List for Resale</h2>
                  <p className={styles.supportLine}>Finish Stripe Express charging setup in Payment before this unit can be listed for resale.</p>
                  <button type="button" className={styles.secondaryButton} onClick={() => navigate('/payment')}>Finish Payment Setup</button>
                </section>
              ) : null}

              {canManage && isForSale ? (
                <section className={styles.block}>
                  <h2>Active Listing</h2>
                  <p className={styles.supportLine}>Your unit is currently listed for resale at {formatPrice(activeListing || product || {})}.</p>
                  <button type="button" className={styles.secondaryButton} onClick={handleCancelListing} disabled={busy}>{busy ? 'Canceling…' : 'Cancel Listing'}</button>
                </section>
              ) : null}

              {canBuy ? (
                <section className={styles.block}>
                  <h2>Buy This Unit</h2>
                  {cards.length ? (
                    <select value={selectedCardId} onChange={(event) => setSelectedCardId(event.target.value)}>
                      {cards.map((card) => <option key={card.id} value={card.id}>{card.brand?.toUpperCase() || 'CARD'} ending in {card.last4}</option>)}
                    </select>
                  ) : <p className={styles.state}>{cardsError || 'No saved cards yet.'}</p>}
                  <button type="button" className={styles.secondaryButton} onClick={handleStartSetup} disabled={setupBusy || !stripePromise}>
                    {setupBusy ? 'Starting…' : 'Add Card'}
                  </button>
                  {setupClientSecret && stripePromise ? (
                    <Elements stripe={stripePromise}>
                      <AddCardForm clientSecret={setupClientSecret} onDone={() => { setSetupClientSecret(''); loadCards(); }} />
                    </Elements>
                  ) : null}
                  <p className={styles.termsLine}>
                    By buying, you agree to the <Link to="/terms">Terms &amp; Purchase Conditions</Link>.
                  </p>
                  <button type="button" className={styles.buyButton} onClick={handleBuy} disabled={busy || !selectedCardId}>
                    {busy ? 'Processing…' : `Buy for ${formatPrice(activeListing || product || {})}`}
                  </button>
                </section>
              ) : null}

              {!canManage && isForSale ? (
                <section className={styles.block}>
                  <h2>Buy This Unit</h2>
                  <p className={styles.state}>
                    {listingBlocked
                      ? listingBlockedReason === 'seller_connect_not_charge_ready'
                        ? 'This resale is temporarily unavailable until the seller completes Stripe Express.'
                        : listingBlockedReason === 'artist_connect_not_charge_ready'
                          ? 'This resale is temporarily unavailable until the original artist completes Stripe Express.'
                          : 'This resale is temporarily unavailable because the payment setup is incomplete.'
                      : 'Resale checkout is temporarily unavailable while tax support is being finalized for secondary sales.'}
                  </p>
                </section>
              ) : null}

              <section className={`${styles.block} ${styles.ownersSection}`.trim()}>
                <div className={styles.sectionTitle}>
                  <div>
                    <h2><FiUsers /> Current Collectors</h2>
                    <p>See who currently owns the other units from this release.</p>
                  </div>
                </div>
                {ownersLoading ? <p className={styles.state}>Loading collectors…</p> : null}
                {ownersError ? <p className={styles.error}>{ownersError}</p> : null}
                {!ownersLoading && !ownersError && owners.length ? (
                  <>
                    <div className={styles.ownerList}>
                      {owners.map((owner) => (
                        <div key={owner.id} className={styles.ownerRow}>
                          <div className={styles.ownerIdentity}>
                            <OwnerAvatar owner={owner} />
                            <div className={styles.ownerCopy}>
                              <strong>{owner.name}</strong>
                              <p>{owner.isAnonymous ? 'Private collector' : owner.ownerUid === String(unit?.ownerUid || '').trim() ? 'Current owner of this unit' : 'Current owner'}</p>
                            </div>
                          </div>
                          <div className={styles.ownerMeta}>
                            <span><FiBox /> Unit #{owner.unitNumber || '—'}</span>
                            <span><FiCalendar /> {formatDate(owner.purchasedAt)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    {ownersHasMore ? (
                      <div className={styles.loadMoreRow}>
                        <button type="button" className={styles.secondaryButton} onClick={() => loadOwners({ afterDoc: ownersCursor })} disabled={ownersLoadingMore}>
                          {ownersLoadingMore ? 'Loading…' : 'Load More Collectors'}
                        </button>
                      </div>
                    ) : null}
                  </>
                ) : null}
                {!ownersLoading && !ownersError && !owners.length ? (
                  <p className={styles.state}>No sold units are recorded for this release yet.</p>
                ) : null}
              </section>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
